import { createFileRoute, Link } from "@tanstack/react-router";
import { Reveal } from "@/components/common/reveal";
import { siteConfig } from "@/config/site";
import { ShieldCheck, Mail, Phone, MapPin } from "lucide-react";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: `Privacy Policy — ${siteConfig.name}` },
      {
        name: "description",
        content:
          "Privacy Policy for Aster Decorz. Understand how we collect, use, and protect your customer data for orders, payments, and botanical deliveries across India.",
      },
      { property: "og:title", content: `Privacy Policy — ${siteConfig.shortName}` },
      {
        property: "og:description",
        content: "How Aster Decorz collects, uses, and secures your personal data.",
      },
      { property: "og:url", content: `${siteConfig.url}/privacy` },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.url}/privacy` }],
  }),
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  const lastUpdated = "October 19, 2026";

  return (
    <div className="pb-28 pt-24 md:pt-28">
      <div className="section-container max-w-4xl">
        <Reveal>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-800">
            <ShieldCheck className="h-4 w-4" />
            <span>Legal & Privacy Compliance</span>
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl">Privacy Policy</h1>
          <p className="mt-2 text-xs text-muted-foreground">
            Effective Date: {lastUpdated} | Aster Decorz (asterdecorz.com)
          </p>
        </Reveal>

        <div className="mt-12 space-y-10 text-sm leading-relaxed text-muted-foreground border-t pt-10">
          <section>
            <h2 className="font-display text-xl text-foreground mb-3">1. Introduction</h2>
            <p>
              Aster Decorz (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) operates{" "}
              <Link to="/" className="text-primary underline">
                asterdecorz.com
              </Link>
              . We are committed to safeguarding the privacy and personal data of our website
              visitors and customers across India. This Privacy Policy outlines what information we
              collect, how we process it, and your legal rights in accordance with the Information
              Technology Act, 2000, the Consumer Protection (E-Commerce) Rules, 2020, and applicable
              advertising standards.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">2. Information We Collect</h2>
            <p className="mb-2">
              When you browse our catalogue, register an account, or make an order, we collect:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong className="text-foreground">Contact & Identity Data:</strong> Full name,
                delivery address, postal PIN code, contact phone number, and email address.
              </li>
              <li>
                <strong className="text-foreground">Order & Transaction Data:</strong> Items
                purchased, billing amount, delivery preference, and payment transaction IDs
                generated by our authorized payment gateway.
              </li>
              <li>
                <strong className="text-foreground">Technical & Device Data:</strong> IP address,
                browser type, device type, operating system, and anonymous interaction metrics
                collected via cookies.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">
              3. Payment Information Security
            </h2>
            <p>
              All customer transactions on Aster Decorz are strictly processed through{" "}
              <strong className="text-foreground">Razorpay</strong>, an authorized, PCI-DSS Level 1
              certified payment gateway. We <strong className="text-foreground">never</strong> store
              or have access to your full credit/debit card numbers, CVVs, net banking credentials,
              or UPI PINs. All financial transactions are encrypted end-to-end using TLS 1.3
              encryption.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">4. How We Use Your Data</h2>
            <p className="mb-2">
              We process your data exclusively for legitimate commercial purposes:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                Processing, packaging, and dispatching botanical decor and artificial plant orders.
              </li>
              <li>
                Providing real-time order tracking and dispatch notifications via SMS or WhatsApp.
              </li>
              <li>Customer service assistance, returns, and delivery issue resolution.</li>
              <li>Website security, prevention of fraudulent chargebacks, and spam protection.</li>
              <li>
                Measuring website performance and conversion analytics via anonymized tracking
                (Google Analytics and Meta Pixel) to improve customer browsing experience.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">
              5. Advertising & Analytics Cookies (Meta & Google)
            </h2>
            <p>
              We use first-party and authorized third-party cookies (such as Google Analytics and
              Meta Pixel) to measure ad performance and deliver relevant botanical collections to
              interested audiences. These tools record anonymized event interactions (such as
              viewing a product or completing checkout) without exposing unencrypted personal
              records to advertisers. You can manage or block cookies through your browser settings
              at any time.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">
              6. Data Sharing & Third Parties
            </h2>
            <p className="mb-2">
              We do not sell, rent, or trade your personal data to third parties. We share data only
              with essential operational partners:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong className="text-foreground">Logistics Partners:</strong> Courier services
                for the physical delivery of your order.
              </li>
              <li>
                <strong className="text-foreground">Payment Processors:</strong> Razorpay for
                verifying secure payments.
              </li>
              <li>
                <strong className="text-foreground">Cloud & Hosting Infrastructure:</strong>{" "}
                Cloudflare and Supabase for fast, secure database operations and global asset
                caching.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">
              7. Your Rights & Data Deletion
            </h2>
            <p>
              Under Indian law, you have the right to request access to the personal data we hold
              about you, request corrections, or request deletion of your account and personal
              records. To exercise your data rights, contact our Privacy Desk at{" "}
              <a
                href={`mailto:${siteConfig.contact.email}`}
                className="text-primary underline font-medium"
              >
                {siteConfig.contact.email}
              </a>
              .
            </p>
          </section>

          <section className="rounded-xl border bg-card p-6">
            <h2 className="font-display text-xl text-foreground mb-3">
              8. Grievance Redressal Officer
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              In accordance with the Information Technology Act, 2000 and rules made thereunder:
            </p>
            <div className="space-y-2 text-xs text-foreground">
              <div className="flex items-center gap-2">
                <span className="font-semibold w-24">Brand:</span>
                <span>Aster Decorz</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                <span>admin@asterdecorz.com</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                <span>+91 9344580982 (Mon–Sat, 10 AM – 7 PM IST)</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Tamil Nadu, India</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
